import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Square, Mic2, Cpu, Repeat } from "lucide-react";
import { RULER_TICK_COUNT, BUSY_LABELS, STEM_STATUS_LABELS, FADE_IN, FADE_FULL, fmtTime, padCh } from "./dawHelpers";
import MicCheckPill from "@/components/daw/MicCheckPill";

/* ──────────────────────────────────────────────────────────────────────
 * TransportDisc — a single tactile circular control (Play/Stop/Record).
 * Pure CSS metallic disc (`.transport-disc` in index.css) lit by a tinted
 * accent. Framer Motion adds the press/spring feel on tap.
 * ────────────────────────────────────────────────────────────────────── */
function TransportDisc({ testId, onClick, ariaLabel, accent, glow, children }) {
  return (
    <motion.button
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      type="button"
      whileTap={{ scale: 0.94 }}
      whileHover={{ scale: 1.04 }}
      transition={{ type: "spring", stiffness: 500, damping: 26 }}
      className="transport-disc relative grid h-12 w-12 place-items-center rounded-full text-white"
      style={{
        "--disc-top": accent.top,
        "--disc-bot": accent.bot,
        "--disc-glow": glow,
      }}
    >
      {children}
    </motion.button>
  );
}

/* Sticky bottom transport — full-width pill that hugs the bottom of the viewport
 * with a frosted-glass backdrop so the timeline / mixer below it remain visible.
 * Spacing on the page is handled by the parent: a 24px spacer div lives at the
 * end of the main column to prevent the last card from being hidden behind it. */
export function TransportBar({ playing, onPlay, onStop, onRecord, recordingIdx, playhead, maxDur, loopEnabled, onToggleLoop, recordingMode, onRecordingModeChange, tracking }) {
  const isRecording = recordingIdx !== null || tracking;
  // iter-158 — User-reported bug: "When press stop on recording button it is
  // not stopping". Pre-fix, the ⏹ STOP disc only stopped playback; users
  // expected it to stop ANY active transport — including recording. Now
  // whenever a recording (per-channel OR multi-track tracking) is active,
  // BOTH the STOP and RECORD discs call `onRecord` to end the take. We
  // also re-style the STOP disc red + pulsing so it's unmistakable.
  const stopHandler = isRecording ? onRecord : onStop;
  const stopLabel = isRecording ? "Stop recording" : "Stop";
  return (
    <div
      data-testid="transport-bar"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-4 pt-2"
    >
      <motion.div
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 30, delay: 0.05 }}
        className="pointer-events-auto glass-card flex flex-wrap items-center justify-between gap-4 rounded-[28px] px-4 py-3"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 -20px 50px -10px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <TransportDisc
            testId="transport-play"
            onClick={onPlay}
            ariaLabel={playing ? "Pause" : "Play"}
            accent={playing
              ? { top: "#fde68a", bot: "#b45309" }
              : { top: "#6ee7b7", bot: "#047857" }}
            glow={playing ? "rgba(252,211,77,0.55)" : "rgba(52,211,153,0.55)"}
          >
            {playing
              ? <Pause className="h-5 w-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" fill="currentColor" />
              : <Play className="h-5 w-5 translate-x-[1px] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" fill="currentColor" />}
          </TransportDisc>

          <TransportDisc
            testId="transport-stop"
            onClick={stopHandler}
            ariaLabel={stopLabel}
            accent={isRecording
              ? { top: "#fda4af", bot: "#9f1239" }
              : { top: "#3a3f5a", bot: "#10131f" }}
            glow={isRecording ? "rgba(244,63,94,0.85)" : "rgba(255,255,255,0.18)"}
          >
            {isRecording ? (
              <span className="relative grid h-5 w-5 place-items-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-md bg-rose-300/70" />
                <Square className="relative h-5 w-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" fill="currentColor" />
              </span>
            ) : (
              <Square className="h-4 w-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" fill="currentColor" />
            )}
          </TransportDisc>

          <TransportDisc
            testId="transport-record"
            onClick={onRecord}
            ariaLabel={isRecording ? "Stop recording" : "Record"}
            accent={isRecording
              ? { top: "#fda4af", bot: "#9f1239" }
              : { top: "#3a3f5a", bot: "#10131f" }}
            glow={isRecording ? "rgba(244,63,94,0.7)" : "rgba(244,63,94,0.4)"}
          >
            {isRecording ? (
              <span className="relative grid h-4 w-4 place-items-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-300/70" />
                <Mic2 className="relative h-4 w-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />
              </span>
            ) : (
              <Mic2 className="h-4 w-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
            )}
          </TransportDisc>

          <div className="ml-1 h-9 w-px bg-white/10" />

          {/* Record mode selector — DRY / MON / PRINT.
              - DRY: classic. Mic recorded raw, FX applied on playback only.
              - MON: hear yourself wet through the FX rack while singing
                     (buffer still saved dry — sweetable later).
              - PRINT: same as MON but the FX get baked INTO the saved buffer
                       (destructive — single-slot undo via engine._undoCache). */}
          <div
            data-testid="record-mode-group"
            role="group"
            aria-label="Recording mode"
            className="flex items-center overflow-hidden rounded-full border border-white/10 bg-black/40"
            style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.5)" }}
          >
            {[
              { id: "dry", label: "Dry", tone: "text-white/65 hover:bg-white/[0.08]", active: "bg-white/[0.16] text-white", title: "Record dry · FX apply on playback only" },
              { id: "monitor", label: "Mon", tone: "text-sky-200/80 hover:bg-sky-500/15", active: "bg-sky-500/30 text-sky-100", title: "Hear yourself THROUGH the FX while singing · saved buffer stays dry" },
              { id: "print", label: "Print", tone: "text-fuchsia-200/80 hover:bg-fuchsia-500/15", active: "bg-fuchsia-500/30 text-fuchsia-100 shadow-[inset_0_0_12px_rgba(232,121,249,0.25)]", title: "BAKE the FX into the saved buffer (destructive · one-click undo)" },
            ].map((m) => {
              const isActive = recordingMode === m.id;
              return (
                <button
                  key={m.id}
                  data-testid={`record-mode-${m.id}`}
                  onClick={() => onRecordingModeChange?.(m.id)}
                  title={m.title}
                  aria-pressed={isActive}
                  disabled={isRecording}
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition disabled:opacity-50 ${isActive ? m.active : m.tone}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          <motion.button
            data-testid="transport-loop"
            onClick={onToggleLoop}
            aria-pressed={loopEnabled}
            title={loopEnabled ? "Loop ON — playback wraps at end" : "Loop OFF"}
            whileTap={{ scale: 0.95 }}
            whileHover={{ y: -1 }}
            className={`studio-pill flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${loopEnabled
              ? "bg-fuchsia-500/25 text-fuchsia-50 ring-1 ring-fuchsia-300/40 shadow-[0_0_16px_rgba(217,70,239,0.35)]"
              : "bg-white/[0.05] text-white/85 hover:bg-white/[0.1]"}`}
          >
            <Repeat className="h-3.5 w-3.5" />Loop
          </motion.button>

          {/* iter-162 — Global Mic Check pill. Lets users verify their
              microphone input level (3-second non-destructive test) from
              the main transport bar, without diving into the Make Music
              modal. Disabled while recording so a stray click doesn't
              clobber the live take. */}
          <MicCheckPill
            testId="transport-mic-check"
            disabled={isRecording}
            compact
          />
        </div>

        {/* iter-158 — Inline recording status pill. When a take is active,
            this prominent red pill appears between the transport controls
            and the time read-out. Clicking it stops the recording — yet
            another easy-to-find target for users who don't see the top
            banner or know which button to press. */}
        {isRecording && (
          <button
            type="button"
            data-testid="transport-recording-pill"
            onClick={onRecord}
            className="group flex animate-pulse items-center gap-2 rounded-full bg-gradient-to-r from-rose-600 to-rose-500 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white shadow-[0_0_24px_rgba(244,63,94,0.6)] ring-1 ring-rose-300/50 transition hover:animate-none hover:from-rose-500 hover:to-rose-400 hover:shadow-[0_0_32px_rgba(244,63,94,0.85)]"
            title="Click to stop recording"
          >
            <span className="relative grid h-2.5 w-2.5 place-items-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-200" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-rose-100" />
            </span>
            <span className="font-display text-[12px] font-bold tracking-wide">
              REC · {tracking
                ? "MULTI-TRACK"
                : `CH ${String((recordingIdx ?? 0) + 1).padStart(2, "0")}`}
            </span>
            <span className="font-mono text-[10px] text-rose-100/85">CLICK TO STOP</span>
          </button>
        )}

        {/* Time read-out — large monospaced LCD-style numerals. */}
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/45 px-4 py-2 font-mono text-[15px] tabular-nums tracking-[0.04em] text-white/75"
          style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.45)" }}>
          <span data-testid="transport-time-current" className="text-fuchsia-100 drop-shadow-[0_0_6px_rgba(232,121,249,0.45)]">{fmtTime(playhead)}</span>
          <span className="text-white/25">/</span>
          <span data-testid="transport-time-total" className="text-white/60">{fmtTime(maxDur)}</span>
        </div>
      </motion.div>
    </div>
  );
}

export function TimelineRuler() {
  return (
    <div className="mb-2 flex h-6 items-end gap-1 rounded-lg border border-white/10 bg-black/30 px-2"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), inset 0 -1px 0 rgba(0,0,0,0.4)" }}>
      {Array.from({ length: RULER_TICK_COUNT }).map((_, i) => (
        <div key={`ruler-${i}`} className="flex flex-1 flex-col items-center">
          <span className={`mb-0.5 font-mono text-[9px] ${i % 4 === 0 ? "text-white/55" : "text-transparent"}`}>{i + 1}</span>
          <span className={`w-px ${i % 4 === 0 ? "h-2 bg-white/50" : "h-1 bg-white/15"}`} />
        </div>
      ))}
    </div>
  );
}

export function FooterBar({ sampleRate, baseLatency }) {
  return (
    <footer className="mt-6 flex items-center justify-between rounded-2xl border border-white/[0.07] bg-white/[0.025] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45 backdrop-blur-md">
      <span>ELPM · Studio · v0.1 — Web Audio · 64ch · 32-bit float</span>
      <span>Sample · {Math.round(sampleRate / 1000)} kHz · Latency · {baseLatency ? `${Math.round(baseLatency * 1000)} ms` : "auto"}</span>
    </footer>
  );
}

export function BusyOverlay({ busy, selectedChannel, stemProgress, onCancelStems }) {
  const isStems = busy === "stems";
  const stemLabel = stemProgress?.status ? STEM_STATUS_LABELS[stemProgress.status] || stemProgress.status : null;
  const downloadInfo = stemProgress?.status === "downloading"
    ? `${stemProgress.stem} · ${stemProgress.index}/${stemProgress.total}`
    : null;

  return (
    <AnimatePresence>
      {busy && busy !== "export" && (
        <motion.div
          initial={FADE_IN}
          animate={FADE_FULL}
          exit={FADE_IN}
          className="fixed inset-0 z-40 grid place-items-center bg-black/65 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="glass-card min-w-[340px] rounded-3xl px-8 py-7 text-center"
          >
            <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full"
              style={{
                background: "radial-gradient(circle at 30% 25%, rgba(232,121,249,0.5), transparent 60%), linear-gradient(180deg, #4f1565, #1a0526)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.6), 0 0 24px rgba(232,121,249,0.4)",
              }}>
              <Cpu className="h-6 w-6 animate-pulse text-fuchsia-100" />
            </div>
            <p className="font-display text-lg font-semibold tracking-tight">{BUSY_LABELS[busy]}</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-white/55">
              Working · on · CH {padCh(selectedChannel)}
            </p>
            {isStems && stemLabel && (
              <p data-testid="stem-progress-label" className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-fuchsia-200">
                {stemLabel}{downloadInfo ? ` · ${downloadInfo}` : ""}
              </p>
            )}
            {isStems && onCancelStems && (
              <button
                data-testid="stem-cancel-btn"
                onClick={onCancelStems}
                className="studio-pill mt-4 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/80 hover:bg-white/[0.12]"
              >
                Cancel
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
